import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAppDispatch } from '../../hooks';
import { useToken } from '../../hooks/useToken';
import { PageCenteredLayout } from '../layout/PageCenteredLayout';
import { fetchEvent, fetchEventAttendees } from '../../reducers/eventsSlice';
import { useEvent } from '../../hooks/useEvent';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import EventAccountPoolAllocationBar from '../event/EventAccountPoolAllocationBar';
import EventHeader from '../event/EventHeader';
import PoolStatus from '../event/PoolStatus';
import NoMatchEvents from './NoMatchEvents';
import AccountsSection from '../event/AccountsSection';
import { type IChildAccount } from '../../interfaces/IChildAccount';
import ReconfigureAccountButton from '../controls/ReconfigureAccountButton';
import FilterAccountsButton from '../event/FilterAccountsButton';
import CopyAccountsButton from '../event/CopyAccountsButton';

export default function EventAccounts() {
  const token = useToken();
  const currentUser = useCurrentUser();
  const { canPublish } = currentUser;
  const role = canPublish ? 'admin' : 'consumer';
  const { eventSlug } = useParams();
  const { event } = useEvent(eventSlug);
  const dispatch = useAppDispatch();
  const [showOnlyUnallocated, setShowOnlyUnallocated] = useState(false);

  // Helper function to check if current user is an instructor for this event
  const isInstructor = (): boolean => {
    if (!event?.attendees || !currentUser.emails?.nodes?.[0]?.email) return false;
    return event.attendees.some(
      (attendee) => attendee.role === 'instructor' && attendee.email === currentUser.emails?.nodes?.[0]?.email,
    );
  };

  console.log('Instructor:', isInstructor());

  // Check if user can update accounts (admin or instructor with reconfigure permission)
  const canUpdateAccounts = (): boolean => {
    return canPublish || ((event?.instructor_reconfigure ?? false) && isInstructor());
  };

  console.log('Can update accounts:', canUpdateAccounts());

  useEffect(() => {
    if (eventSlug) {
      const fetchData = () => {
        dispatch(fetchEvent({ token, role, slug: eventSlug }))
          .then(() => {
            dispatch(fetchEventAttendees({ token, role, slug: eventSlug })).catch((err) => {
              console.log('Fetch eventAttendees error:', err);
            });
          })
          .catch((err) => {
            console.log('Fetch event error:', err);
          });
      };

      fetchData();

      const refreshInterval = setInterval(fetchData, 60000);

      return () => {
        clearInterval(refreshInterval);
      };
    }
  }, [eventSlug, role, token, dispatch]);

  const getAllChildAccountIds = (): number[] => {
    return (event as any)?.child_accounts?.map((account: IChildAccount) => account.id) || [];
  };

  if (!event) {
    return <NoMatchEvents />;
  }

  const dateFormat = 'dddd D MMM YYYY';
  const formattedStartDate = dayjs(event.start_datetime).format(dateFormat);
  const totalReadyAccounts = event.account_pool?.total_ready_child_accounts ?? 0;
  const allocatedAccounts = event.account_pool?.total_allocated_child_accounts ?? 0;
  const allocatedPercentage = totalReadyAccounts === 0 ? 0 : (allocatedAccounts / totalReadyAccounts) * 100;

  return (
    <>
      <main>
        <EventHeader
          eventName={event.name}
          eventSlug={event.slug}
          eventLocation={event.location}
          formattedStartDate={formattedStartDate}
          activeTab="preparation"
          titleSuffix={<> / Preparation</>}
          headerActions={
            <ReconfigureAccountButton
              accountIds={getAllChildAccountIds()}
              eventSlug={eventSlug}
              size="mediumPlus"
              intent="primary"
              label="Update all accounts"
              className="flex items-center gap-2 hover:bg-dataops-yale-blue/90 transition-colors"
              showConfirmation={true}
              canUpdate={canUpdateAccounts()}
            />
          }
        />

        <PageCenteredLayout extraClassName="py-8">
          <div className="w-full grid grid-cols-2 items-start gap-x-24 text-dataops-yale-blue">
            <EventAccountPoolAllocationBar allocatedPercentage={allocatedPercentage} label="Account Pool" />
            <div className="flex flex-col gap-y-1 text-right text-sm">
              <p>Total Accounts: {totalReadyAccounts}</p>
              <p>Assigned to User Accounts: {allocatedAccounts}</p>
              <p>
                Pool Status: <PoolStatus status={event.account_pool?.status ?? 'No status available'} />
              </p>
            </div>
          </div>
        </PageCenteredLayout>

        {(event as any).child_accounts && (event as any).child_accounts.length > 0 && (
          <PageCenteredLayout extraClassName="py-8">
            {(() => {
              const childAccounts = (event as any).child_accounts as IChildAccount[];
              const allocatedAccounts = childAccounts.filter((account) => account.has_been_allocated);
              const unallocatedAccounts = childAccounts.filter((account) => !account.has_been_allocated);

              return (
                <React.Fragment>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                      <FilterAccountsButton
                        showOnlyUnallocated={showOnlyUnallocated}
                        setShowOnlyUnallocated={setShowOnlyUnallocated}
                      />
                      <CopyAccountsButton
                        allAccounts={childAccounts}
                        unallocatedAccounts={unallocatedAccounts}
                        showOnlyUnallocated={showOnlyUnallocated}
                        organizationAccount={event.organization_account}
                      />
                    </div>
                    <div className="flex bg-dataops-saffron-yellow rounded-full">
                      {showOnlyUnallocated && (
                        <div className="flex justify-center items-center h-6 px-2 rounded-full bg-dataops-saffron-yellow text-xs">
                          Unallocated: {unallocatedAccounts.length}
                        </div>
                      )}
                      <div className="flex justify-center items-center h-6 px-2 rounded-full bg-dataops-platinum-gray text-xs">
                        Total: {allocatedAccounts.length + unallocatedAccounts.length}
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-4">
                    {!showOnlyUnallocated && allocatedAccounts.length > 0 && (
                      <AccountsSection
                        accounts={allocatedAccounts}
                        title="Assigned to User Accounts"
                        canUpdate={canUpdateAccounts()}
                        eventSlug={eventSlug}
                        organizationAccount={event.organization_account}
                      />
                    )}
                    {unallocatedAccounts.length > 0 && (
                      <AccountsSection
                        accounts={unallocatedAccounts}
                        title="Unassigned to User Accounts"
                        canUpdate={canUpdateAccounts()}
                        eventSlug={eventSlug}
                        organizationAccount={event.organization_account}
                      />
                    )}
                  </ul>
                </React.Fragment>
              );
            })()}
          </PageCenteredLayout>
        )}
      </main>
    </>
  );
}
